import React, { useEffect, useMemo, useState } from "react";
import AdminTable from "common/AdminTable";
import axiosInstance from "apis/axiosConfig";

const statusBadgeStyle = {
  background: "#dcfce7",
  color: "#15803d",
  borderRadius: "999px",
  padding: "6px 12px",
  fontSize: "12px",
  fontWeight: 700,
  display: "inline-block",
};

const inputStyle = {
  width: "100%",
  border: "1px solid #ced4da",
  borderRadius: "6px",
  padding: "10px 12px",
  fontSize: "14px",
  outline: "none",
};

const modalButtonStyle = {
  border: "none",
  borderRadius: "6px",
  padding: "10px 18px",
  fontSize: "14px",
  fontWeight: 600,
  cursor: "pointer",
};

const ModalShell = ({ title, children, onClose }) => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0, 0, 0, 0.45)",
      zIndex: 9999,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "16px",
    }}
  >
    <div
      className="modal-content border-0 shadow"
      style={{ width: "100%", maxWidth: "560px", borderRadius: "12px" }}
    >
      <div className="modal-header bg-light">
        <h5 className="modal-title fw-semibold text-dark mb-0">{title}</h5>
        <button className="close btn text-danger" onClick={onClose} style={{ fontSize: "1.5rem" }}>
          &times;
        </button>
      </div>
      <div className="modal-body">{children}</div>
    </div>
  </div>
);

const SubcategoryList = () => {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [subcategoryName, setSubcategoryName] = useState("");

  const categoryOptions = useMemo(
    () => categories.map((item) => ({ id: item.id, name: item.name })),
    [categories]
  );

  const fetchData = async () => {
    setLoading(true);
    try {
      const [categoryRes, subcategoryRes] = await Promise.all([
        axiosInstance.get("/categories"),
        axiosInstance.get("/subcategories"),
      ]);
      setCategories(Array.isArray(categoryRes.data) ? categoryRes.data : []);
      setSubcategories(Array.isArray(subcategoryRes.data) ? subcategoryRes.data : []);
    } catch (error) {
      console.error("Failed to load subcategories:", error);
      setCategories([]);
      setSubcategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openEditModal = (row) => {
    setEditingSubcategory(row);
    setSelectedCategory(String(row.category_id || ""));
    setSubcategoryName(row.name || "");
  };

  const closeEditModal = () => {
    setEditingSubcategory(null);
    setSelectedCategory("");
    setSubcategoryName("");
  };

  const handleUpdate = async () => {
    const trimmedName = subcategoryName.trim();
    if (!editingSubcategory?.id || !selectedCategory || !trimmedName) return;

    try {
      await axiosInstance.patch(`/subcategories/${editingSubcategory.id}`, {
        category_id: Number(selectedCategory),
        name: trimmedName,
        status: editingSubcategory.status || "Active",
      });
      await fetchData();
      closeEditModal();
    } catch (error) {
      console.error("Failed to update subcategory:", error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this subcategory?")) return;

    try {
      await axiosInstance.delete(`/subcategories/${id}`);
      await fetchData();
    } catch (error) {
      console.error("Failed to delete subcategory:", error);
    }
  };

  const tableHeaders = [
    { Header: "#", accessor: "id" },
    { Header: "Parent Category", accessor: "parent" },
    { Header: "Subcategory Name", accessor: "name" },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ row }) => (
        <span style={statusBadgeStyle}>{row.original.status || "Active"}</span>
      ),
    },
    {
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => (
        <div className="d-flex justify-content-center flex-wrap" style={{ gap: "8px" }}>
          <button className="btn btn-sm btn-info text-white" onClick={() => openEditModal(row.original)}>
            Edit
          </button>
          <button className="btn btn-sm btn-danger" onClick={() => handleDelete(row.original.id)}>
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="container-fluid mt-5">
        <div className="mainheadig mx-4">
          <h4 className="text-white font-weight-bold">Subcategory List</h4>
        </div>

        <div className="mx-4 mb-2 text-muted">{subcategories.length} records</div>

        {loading ? (
          <div className="mx-4 text-muted">Loading subcategories...</div>
        ) : subcategories.length > 0 ? (
          <AdminTable tableHeaders={tableHeaders} tableData={subcategories} />
        ) : (
          <div className="mx-4 text-muted">No subcategory records found.</div>
        )}
      </div>

      {editingSubcategory && (
        <ModalShell title="Edit Subcategory" onClose={closeEditModal}>
          <div className="form-group mb-3">
            <label htmlFor="subcategory-category" className="admin-label">
              Parent Category
            </label>
            <select
              id="subcategory-category"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={inputStyle}
            >
              <option value="">Select category</option>
              {categoryOptions.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group mb-3">
            <label htmlFor="subcategory-name" className="admin-label">
              Subcategory Name
            </label>
            <input
              id="subcategory-name"
              type="text"
              value={subcategoryName}
              onChange={(e) => setSubcategoryName(e.target.value)}
              style={inputStyle}
            />
          </div>

          <div className="d-flex justify-content-end" style={{ gap: "10px" }}>
            <button
              onClick={closeEditModal}
              style={{ ...modalButtonStyle, background: "#e9ecef", color: "#495057" }}
            >
              Cancel
            </button>
            <button
              onClick={handleUpdate}
              style={{ ...modalButtonStyle, background: "#198754", color: "#fff" }}
            >
              Update
            </button>
          </div>
        </ModalShell>
      )}
    </>
  );
};

export default SubcategoryList;
